import React, { useEffect, useState, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputNumber } from 'primereact/inputnumber';
import { Save, Plus, X, Shield, Users, Zap } from "lucide-react"; 
import { IconTextButton } from "../../../components/ButtonComponents";
import { Dropdown } from "primereact/dropdown"; 
import { RoleType, RolePayload, RoleGroup, PermissionGroupDef } from "../../../types/role";
import { DynamicPermissionTree } from "../../../components/DynamicPermissionTree";  
import { toast } from "react-toastify";
import { useRoleManagementStore } from "../../../store/roleManagementStore";

interface RoleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  role: RoleType | null;
  onSave: (data: RolePayload) => Promise<any>;
  roleGroups: RoleGroup[];
}

const initialData: RolePayload = {
  name: "",
  level: 1,
  roleGroupId: null,
};

export const RoleDialog: React.FC<RoleDialogProps> = ({
  isOpen,
  onClose,
  role,
  onSave,
  roleGroups,
}) => {
  const { fetchActiveModules, updateRolePermissions, fetchPermissionsSchema } = useRoleManagementStore();
  
  const [formData, setFormData] = useState<RolePayload>(initialData);
  const [activeModules, setActiveModules] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<any[]>([]);
  const [permissionsSchema, setPermissionsSchema] = useState<PermissionGroupDef[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    fetchActiveModules().then(setActiveModules);
    fetchPermissionsSchema().then(setPermissionsSchema);

    if (role) {
      setFormData({
        name: role.name || "",
        level: role.level || 1,
        roleGroupId: role.roleGroupId || null,
      });
      setPermissions(role.permissions || []);
    } else {
      setFormData(initialData);
      setPermissions([]);
    }
  }, [isOpen, role]);

  const roleGroupOptions = useMemo(() => [
    { label: 'Rol Grubu Yok', value: null },
    ...roleGroups.map(group => ({
      label: group.name,
      value: group.id
    }))
  ], [roleGroups]);

  const handleChange = (field: keyof RolePayload, value: any) => {
    setFormData((p) => ({ ...p, [field]: value }));
  };

 // RoleDialog.tsx içindeki handleSubmit metodu

const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!formData.name) {
    toast.error("Rol adı zorunludur.");
    return;
  }

  setLoading(true);
  try {
    // 1. Rol Bilgilerini Kaydet
    const savedRole = await onSave(formData);

    // 2. Yetki Matrisini Kaydet
    const targetRoleId = role?.id || savedRole?.id;

    if (targetRoleId) {
      // DİKKAT: Backend'in kızdığı 'id' ve 'roleId' alanlarını temizliyoruz
      const cleanedPermissions = permissions.map(({ id, roleId, ...rest }) => ({
        ...rest
        // rest içinde sadece: module, canRead, canCreate, canUpdate, canDelete, canExport kalır
      }));

      await updateRolePermissions(targetRoleId, cleanedPermissions);
    }
    
    onClose();
    toast.success("Rol ve yetkiler başarıyla kaydedildi.");
  } catch (error) {
    console.error("Kayıt hatası:", error);
    toast.error("Yetkiler kaydedilirken bir hata oluştu.");
  } finally {
    setLoading(false);
  }
};

  return (
    <Dialog
      header={
        <div className="flex items-center gap-2 text-primary">
          <Shield className="w-5 h-5" />
          <span className="font-bold">{role ? "Rolü Düzenle" : "Yeni Rol Oluştur"}</span>
        </div>
      }
      visible={isOpen}
      onHide={onClose}
      modal
      maximizable
      className="w-full lg:w-10/12 xl:w-8/12"
      footer={
        <div className="flex justify-end gap-3 border-t p-4">
          <IconTextButton onClick={onClose} icon={X} className="btn-cancel">İptal</IconTextButton>
          <IconTextButton onClick={handleSubmit} icon={role ? Save : Plus} className="btn-new">
            {role ? "Güncelle" : "Ekle"}
          </IconTextButton>
        </div>
      }
      contentClassName="p-0"
    >
      <div className="flex flex-col lg:flex-row w-full h-full min-h-[60vh] bg-bgAlt text-text">
        
        {/* Left Column - Basic Info */}
        <div className="w-full lg:w-1/3 p-6 border-b lg:border-b-0 lg:border-r border-borderColor space-y-6 bg-card">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2 mb-4 text-text">
              <Zap className="w-5 h-5 text-yellow-500" />
              Temel Bilgiler
            </h3>
            <p className="text-xs text-secondary mb-6">Rolün temel özelliklerini ve seviyesini belirleyin.</p>
          </div>

          <div className="space-y-5">
            <div className="flex flex-col gap-2">
              <label htmlFor="name" className="text-sm font-semibold">Rol Adı *</label>
              <InputText
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Örn: Plasiyer"
                className="w-full"
              />
            </div>
            
            <div className="flex flex-col gap-2">
              <label htmlFor="level" className="text-sm font-semibold">Yetki Seviyesi *</label>
              <InputNumber
                id="level"
                value={formData.level}
                onValueChange={(e) => handleChange('level', e.value)}
                min={1} max={999}
                showButtons
                buttonLayout="horizontal"
                className="w-full"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="roleGroup" className="text-sm font-semibold">Rol Grubu</label>
              <Dropdown
                id="roleGroup"
                value={formData.roleGroupId}
                options={roleGroupOptions}
                onChange={(e) => handleChange('roleGroupId', e.value)}
                placeholder="Grup Seçiniz"
                className="w-full"
              />
            </div>
          </div>

          {role && (
            <div className="mt-8 flex items-center gap-3 p-4 bg-primary/10 border border-primary/20 rounded-xl">
              <div className="p-2 bg-primary/20 rounded-lg text-primary">
                <Users className="w-5 h-5"/>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-primary/70 font-medium">Aktif Kullanıcı</span>
                <span className="text-sm font-bold text-primary">
                  Bu role sahip {role.users?.length || 0} kişi var
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Permissions */}
        <div className="w-full lg:w-2/3 p-6 bg-bgAlt">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold flex items-center gap-2 text-text">
                <Shield className="w-5 h-5 text-primary" />
                Yetki Matrisi
              </h3>
              <p className="text-xs text-secondary mt-1">Bu role hangi modüllerin ve işlemlerin açık olacağını seçin.</p>
            </div>
          </div>

          <div className="bg-card rounded-xl border border-borderColor p-2 shadow-sm">
            <DynamicPermissionTree 
              permissionsSchema={permissionsSchema}
              activeModules={activeModules} 
              permissions={permissions} 
              onChange={setPermissions} 
            />
          </div>
        </div>

      </div>
    </Dialog>
  );
};